
/**
 * @typedef {import("@/types/user/user").User} User
 */
import { mapActions, mapGetters, mapMutations } from "vuex";
import logo from "~/assets/images/logo.png";
import { ROLE } from "~/assets/js/constants";
import { isAllowedTo } from "~/assets/js/utils";
import NavHeaderTransformer from "~/transformers/NavHeaderTransformer";
import { resolveActiveTabId } from "~/transformers/NavTabsTransformer";

const USER_CONFIG_ROLES = [ROLE.ADMIN, ROLE.USER, ROLE.MANAGER];

export default {
  name: "AppHeader",
  components: {
    UserConfiguration: () => import("~/components/configuration/UserConfiguration.vue"),
    ClaudiaFileDownloader: () => import("~/components/asideComponents/claudiaFileDownloader.vue"),
    DownloadManager: () => import("~/components/file/DownloadManager.vue"),
  },
  data() {
    return {
      isUserConfigOpen: false,
      // id of the aux action whose dispatch is in flight (shows a spinner)
      loadingActionId: null,
      // agency tabs are rendered by the separate NavTabs strip, not the header
      tools: [],
    };
  },
  computed: {
    ...mapGetters({
      mainNav: "application/getMainNav",
      loggedRole: "application/getLoggedRole",
      loggedUser: "application/getLoggedUser",
      arePendingDownloads: "application/arePendingDownloads",
      areDownloadsCompleted: "application/areDownloadsCompleted",
      isInAgency: "application/isInAgency",
      isInFederatedInstance: "application/isInFederatedInstance",
      isSingleAgencyUser: "application/isSingleAgencyUser",
    }),
    hasAgency() {
      /** @type {User} */
      const { loggedUser } = this;
      return Boolean(loggedUser?.agencies?.length > 0);
    },
    productInfo() {
      return NavHeaderTransformer.toProductInfo(logo);
    },
    userInfo() {
      return NavHeaderTransformer.toUserInfo(this.loggedUser);
    },
    mainRoute() {
      return NavHeaderTransformer.toMainRoute(this.mainNav, this.$t.bind(this));
    },
    routeGroups() {
      return NavHeaderTransformer.toRouteGroups(this.mainNav, this.$t.bind(this));
    },
    auxiliaryActions() {
      const actions = NavHeaderTransformer.toAuxiliaryActions(
        {
          loggedRole: this.loggedRole,
          hasAgency: this.hasAgency,
          arePendingDownloads: this.arePendingDownloads,
          areDownloadsCompleted: this.areDownloadsCompleted,
        },
        this.$t.bind(this),
      );
      return actions.map((action) =>
        action.id === this.loadingActionId ? { ...action, loading: true } : action,
      );
    },
    /**
     * s2 highlights with an exact match, but the old NavLink used prefix
     * matching (e.g. `/agencies/123` highlights `/agencies`). Pick the longest
     * route id that prefixes the current path; `/` only matches exactly.
     */
    activeRoute() {
      // Inside an opened agency or federated instance the management tabs must not
      // be highlighted, so pressing one navigates back out of the context. Single
      // agency users have a permanent selectedAgency (set at login, never an opened
      // tab), so they keep the normal highlight.
      if ((this.isInAgency && !this.isSingleAgencyUser) || this.isInFederatedInstance) return "";
      const ids = [this.mainRoute, ...this.routeGroups.flatMap((g) => g.routes)]
        .filter(Boolean)
        .map((r) => r.id);
      return resolveActiveTabId(ids, this.$route.path) ?? "";
    },
  },
  methods: {
    ...mapActions({
      navigateToConfig: "application/navigateToConfig",
      navigateToFaqs: "application/navigateToFaqs",
      downloadDocumentation: "application/downloadDocumentation",
      resetSelectedAgency: "application/resetSelectedAgency",
      logout: "authentication/logout",
    }),
    ...mapMutations({
      resetSelectedInstance: "application/RESET_SELECTED_INSTANCE",
    }),
    onRoute(id) {
      // Mirror the old TheNav behaviour: navigating via the main nav clears the
      // agency context (no-op for single-agency users). Agency tabs that would
      // set the context are deferred to the `tools` prop. The federated instance
      // context must be cleared too; otherwise `isInFederatedInstance` stays true
      // and `activeRoute` would remain empty, never highlighting the target tab.
      this.resetSelectedAgency();
      this.resetSelectedInstance();
      if (this.$route.path !== id) this.$router.push(id);
    },
    async onAction(id) {
      switch (id) {
        case "config":
          await this.runWithLoading(id, this.navigateToConfig);
          break;
        case "faqs":
          this.navigateToFaqs();
          break;
        case "documentation":
          await this.runWithLoading(id, this.downloadDocumentation);
          break;
        case "tech-support":
          this.$emit("tech-support-panel-open");
          break;
        // download-manager & claudia-file-downloader are popover slots only.
        default:
          break;
      }
    },
    async runWithLoading(id, dispatch) {
      this.loadingActionId = id;
      try {
        await dispatch();
      } finally {
        this.loadingActionId = null;
      }
    },
    onUser() {
      if (!isAllowedTo(USER_CONFIG_ROLES, this.loggedRole)) return;
      this.isUserConfigOpen = !this.isUserConfigOpen;
    },
    onLogout() {
      this.logout();
    },
  },
};
